import { IllustratedRouteLayout, type ResponsiveDevice } from "@src/common/IllustratedRouteLayout";
import { BodyText, HeadingDiv } from "@src/common/styled-components";
import { APP_ROUTES } from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context";
import { useTimerContext } from "@src/context/TimerContext";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import ClientLogsService from "@src/services/client-logs.service";
import { navigateToContactUs } from "@src/utils/contact-us-navigation";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { useEffect } from "react";

const StyledLink = styled("a", {
  color: "$app.color.text.primary",
  textDecoration: "underline",
  cursor: "pointer",
  "&:hover": {
    color: "$app.color.text.primary",
    textDecoration: "none",
  },
});

export const Error = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const isTablet = useMediaQuery(MEDIA_QUERIES.tabletMax);
  const { stopTimer } = useTimerContext();
  const navigate = useNavigateWithUTM();
  const { data } = useApplicationContext();

  useEffect(() => {
    // Stop the timer when landing on error page
    stopTimer();
    ClientLogsService.logWebAction(
      data.userUuid || "unknown",
      "ErrorPage",
      "Timer stopped due to error page visit",
    );
  }, [stopTimer]);

  const handleReturnToGetStarted = () => {
    navigate(APP_ROUTES.GET_STARTED);
  };

  let device: ResponsiveDevice = "desktop";
  if (isMobile) {
    device = "mobile";
  } else if (isTablet) {
    device = "tablet";
  }

  return (
    <IllustratedRouteLayout metaTitle="Error" device={device}>
      <HeadingDiv device={device}>We're having trouble</HeadingDiv>
      <BodyText device={device}>
        Please wait a moment and try again, or{" "}
        <StyledLink
          id="obc-error-contact-us-link"
          data-testid="test-id-error-contact-us-link"
          data-obc-label="Contact Us Link on Error Page"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            navigateToContactUs(navigate, data.userUuid, undefined, false);
          }}
        >
          contact us
        </StyledLink>
        .
      </BodyText>
      <Button
        data-testid="test-id-error-return-to-get-started-button"
        variant="filled"
        size={isMobile ? "$lg" : "$md"}
        id="obc-error-return-to-get-started-btn"
        data-obc-label="Return to Get Started Button on Error Page"
        onClick={handleReturnToGetStarted}
        css={{
          minWidth: isMobile ? "100%" : "300px",
        }}
      >
        Return to Get Started
      </Button>
    </IllustratedRouteLayout>
  );
};
